import { cn } from '@/utils/cn';

/** Named stops from the Wonder ramp; any CSS colour also works. */
const TONES = {
  iris: 'var(--color-iris-500)',
  lilac: 'var(--color-iris-300)',
  deep: 'var(--color-iris-700)',
  white: 'var(--color-on-night)',
};

/**
 * The ambient orb that sits behind the marketing sections.
 *
 * In Figma this is a 520px circle filled with a tiled `image.png`, dropped to
 * 15% opacity under a 64px layer blur. At that opacity behind that much blur
 * the source bitmap contributes nothing but a soft falloff, so this draws a
 * radial gradient instead of shipping the asset: same result, no image
 * request, and the colour follows a token rather than being baked into a PNG.
 * Pass `src` if a particular orb really does need the bitmap.
 *
 * Purely decorative -- hidden from assistive tech and transparent to pointer
 * events. It needs a `relative` ancestor to position against, and that
 * ancestor almost always wants `overflow-hidden` too, since these are placed
 * to hang off the edge of a section.
 *
 * `size` is the *unblurred* box, matching Figma's width/height. The blur
 * bleeds roughly `blur` px past it on every side, which is what makes the edge
 * read as light rather than as a circle -- budget for that when placing one,
 * and note that `left`/`top` still address the sharp box, not the halo.
 */
export function Glow({
  size = 520,
  tone = 'iris',
  opacity = 0.15,
  blur = 64,
  src,
  className,
  style,
}) {
  return (
    <div
      aria-hidden="true"
      className={cn('pointer-events-none absolute rounded-full', !src && 'wonder-glow', className)}
      style={{
        '--glow-color': TONES[tone] ?? tone,
        width: size,
        height: size,
        opacity,
        filter: `blur(${blur}px)`,
        ...(src && { backgroundImage: `url(${src})`, backgroundRepeat: 'repeat' }),
        ...style,
      }}
    />
  );
}
